/**
 * "Xm ago" style relative label that coarsens as time passes: "just now" under
 * a minute, then minutes, then hours, then days. Used for the dashboard's
 * "Updated …" freshness stamp.
 *
 * Pure over both timestamps (milliseconds since the epoch) so it's
 * deterministic to test; the caller ticks `nowMs` to keep the label live. A
 * `nowMs` earlier than `fromMs` (clock skew) clamps to "just now" rather than
 * showing a negative age.
 */
export function relativeAgo(fromMs: number, nowMs: number): string {
  const seconds = Math.max(0, Math.floor((nowMs - fromMs) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * How long to wait before re-evaluating an "Updated X ago" label, given the
 * data's current age in ms. The cadence matches the label's own granularity so
 * we never tick faster than the text can change: every 5 minutes while it reads
 * in minutes, every hour once it reads in hours, every day once it reads in
 * days. Callers reschedule from this after each tick so it steps down through
 * the coarser cadences as the data ages.
 */
export function tickIntervalMs(ageMs: number): number {
  if (ageMs < HOUR_MS) return 5 * MINUTE_MS;
  if (ageMs < DAY_MS) return HOUR_MS;
  return DAY_MS;
}
